import { Activity, Lock, Sparkles } from "lucide-react";
// import { useCandidate } from "../../providers/CandidateProvider";
import CandidateAssessmentDetails from "./CandidateAssessmentDetails";
import CandidateRoleMatchDetails from "./CandidateRoleMatchDetails";
import { FitRing, FitVerdict, StatGrid, StatRow } from "./CandidateFitPrimitives";
import React from "react";

const RING_PURPLE = '#5F3DB1';

/**
 * Demand badges earn a glyph that matches their heat: a flame for the top
 * tier, a bolt for the tier below it. Keyed off the label because that is all
 * the API sends — `transformed_demand()` derives the badge server-side.
 */
const DemandIcon = ({ badge }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-3 w-3 shrink-0">
        {badge === 'Very High Demand' ? (
            <path d="M12 2c.4 2.4 1.9 3.7 3.1 5.1C16.4 8.7 18 10.4 18 13.4a6 6 0 0 1-12 0c0-1.7.7-2.9 1.5-3.8.2 1 .9 1.7 1.8 1.9.1-2.6 1.1-4.3 2.5-6.5.4-.6.6-1.4.7-3Z" />
        ) : (
            <path d="M13.5 2 4 13.2h5.6L9 22l9.5-11.5H12.9L13.5 2Z" />
        )}
    </svg>
);

/**
 * A badge and the numbers behind it, on one line, centred as a pair.
 *
 * A flex row rather than a shared grid column: on a grid both badges took the
 * width of the longer one, leaving "High Demand" padded out to the width of
 * "Emerging Performer". Each pill hugs its own text instead.
 */
const BadgeRow = ({ icon, label, children }) => (
    <div className="self-center flex flex-row items-center gap-1">
        {label && (
            <span className="inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full border border-[#E2E3EA] bg-white px-2 py-[3px] text-[10px] font-medium leading-[1.2] tracking-[-0.01em] text-[#0E0E14]">
                <span className="inline-flex text-[#6C7280]">{icon}</span>
                {label}
            </span>
        )}

        {/* Deliberately smaller than the badge beside it: this is supporting
            evidence, and at any larger size the longest line — "12 Proposal
            Invites · 1 Recent Like" — wraps and pushes the panel past its
            locked height. */}
        <span className="min-w-0 text-left text-[8.5px] font-medium leading-[1.3] text-[#6C7280]">
            {children}
        </span>
    </div>
);

/**
 * A single figure in a badge's evidence line. Dropped entirely at zero — "0
 * Invites" beside a High Demand badge reads as a contradiction.
 */
const BadgeStat = ({ count, noun, suffix, isFirst }) => {
    if (!count) {
        return null;
    }

    return (
        <>
            {!isFirst && ' • '}
            <span className="whitespace-nowrap">
                <b className="font-semibold text-[#121217]">{count}{suffix}</b>
                {` ${noun}`}
            </span>
        </>
    );
};

const TrophyIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        className="h-3 w-3 shrink-0"
    >
        <path d="M6 4h12v3a6 6 0 0 1-12 0V4Z" />
        <path d="M6 6H3.6v1A3.4 3.4 0 0 0 6 10.4M18 6h2.4v1A3.4 3.4 0 0 1 18 10.4" />
        <path d="M9.6 20l.5-2.7a1 1 0 0 1 1-.8h1.8a1 1 0 0 1 1 .8l.5 2.7M12 13v3" />
    </svg>
);

/**
 * Where the candidate sits in the invite flow, shown above the locked panel.
 * Null when there is no status worth calling out yet.
 */
const lockedStatusLabel = (candidate) => {
    if (candidate?.isPendingShortlisted?.()) {
        return 'Invitation Sent';
    }

    return candidate?.statusLabel?.() || null;
};

/**
 * Two different reasons the panel can be locked, and each points at a
 * different next action: an invited candidate just needs time, an uninvited
 * one needs the client to press the button below.
 */
const LockedPanel = ({ candidate }) => {
    const firstName = candidate?.first_name || 'this candidate';
    const hasBeenInvited = candidate?.hasBeenInvited?.();
    const statusLabel = hasBeenInvited ? lockedStatusLabel(candidate) : null;

    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-[7px] px-4 py-5 text-center">
            {statusLabel && (
                <span className="rounded-full bg-[#F1ECFB] px-2.5 py-[3px] text-[9px] font-bold uppercase tracking-[0.09em] text-primary">
                    {statusLabel}
                </span>
            )}

            <Lock className="h-[26px] w-[26px] text-[#B7ABDF]" strokeWidth={1.5} />

            <p className="m-0 text-[13px] font-bold tracking-[-0.01em] text-[#0E0E14]">
                Hire Confidence
            </p>

            <p className="m-0 max-w-[230px] text-[11px] leading-[1.45] text-[#595D68]">
                {hasBeenInvited ? (
                    <>Unlocks when {firstName} submits a proposal</>
                ) : (
                    <><b className="font-bold text-primary">Invite {firstName}</b> to unlock the full AI hire recommendation</>
                )}
            </p>
        </div>
    );
};

/**
 * The proposal is in and the assessment isn't back yet.
 *
 * This is a genuinely different state from the padlock above, and conflating
 * the two was actively misleading: a candidate who had just proposed was being
 * told their score "unlocks when they submit a proposal", which reads as though
 * the client is still waiting on the talent when the talent has already done
 * their part. Nobody has anything left to do here — the work is ours, it is
 * under way, and the only useful instruction is to come back.
 *
 * So: no padlock, because nothing is being withheld. The AI glyph instead,
 * pulsing, because the thing that is happening is the assessment running.
 */
const PendingPanel = ({ candidate }) => {
    const firstName = candidate?.first_name || 'this candidate';

    return (
        <div className="flex flex-1 flex-col items-center justify-center gap-[7px] px-4 py-5 text-center">
            <span className="rounded-full bg-[#F1ECFB] px-2.5 py-[3px] text-[9px] font-bold uppercase tracking-[0.09em] text-primary">
                {candidate?.statusLabel?.() || 'Proposal Received'}
            </span>

            <Sparkles className="h-[26px] w-[26px] animate-pulse text-primary" strokeWidth={1.5} />

            <p className="m-0 text-[13px] font-bold tracking-[-0.01em] text-[#0E0E14]">
                Assessment in Progress
            </p>

            {/* No promised duration. The run is a queue of live AI requests and
                can be minutes or longer under load, and a time we miss is worse
                than no time at all. */}
            <p className="m-0 max-w-[230px] text-[11px] leading-[1.45] text-[#595D68]">
                Our AI is reviewing {firstName}'s proposal against this role.
                Check back shortly for their full hire recommendation.
            </p>
        </div>
    );
};

/**
 * The talent's earned badges and the numbers behind them. Shared by the full
 * panel and by the history-only panel used on surfaces that never see a
 * proposal.
 */
const BadgeList = ({ performance, demand }) => (
    <div className="flex w-full flex-col gap-1">
        {/* Talent who have the numbers but haven't cleared a badge threshold
            still get a chip — a neutral one. Without it their stats float
            unlabelled beside a badged row and read as an error. */}
        {performance && (
            <BadgeRow icon={<TrophyIcon />} label={performance.badge || 'Emerging Performer'}>
                <BadgeStat
                    isFirst
                    count={performance.completed_projects_count}
                    noun={TB.pluralize('Project', performance.completed_projects_count)}
                />
                <BadgeStat
                    count={performance.unique_clients_count}
                    noun={TB.pluralize('Client', performance.unique_clients_count)}
                />
                {/* Dropped at zero, like the counts above it — a talent who
                    hasn't been rehired yet is usually one nobody has had the
                    chance to rehire, not a verdict worth printing. */}
                <BadgeStat
                    count={performance.rehires_count}
                    noun={TB.pluralize('Rehire', performance.rehires_count)}
                />
            </BadgeRow>
        )}

        {/* No fallback label here, unlike performance above: `demandSummary()`
            only returns an object once a badge has been earned. */}
        {demand && (
            <BadgeRow icon={<DemandIcon badge={demand.badge} />} label={demand.badge}>
                <BadgeStat
                    isFirst
                    count={demand.job_invites_count}
                    noun={TB.pluralize('Proposal Invite', demand.job_invites_count)}
                />
                <BadgeStat
                    count={demand.job_likes_count}
                    noun={TB.pluralize('Recent Like', demand.job_likes_count)}
                />
            </BadgeRow>
        )}
    </div>
);

/**
 * What the tab shows where a proposal can never happen — browsing talent
 * rather than reviewing candidates for a role.
 *
 * There is nothing to unlock in that context, so a padlock would be a dead
 * end. The platform track record stands on its own instead, and the empty
 * state explains an absence rather than a restriction.
 */
const HistoryOnlyPanel = ({ candidate, performance, demand }) => {
    if (!performance && !demand) {
        return (
            <div className="flex flex-1 flex-col items-center justify-center gap-[7px] px-4 py-5 text-center">
                <Activity className="h-[26px] w-[26px] text-[#B7ABDF]" strokeWidth={1.5} />

                <p className="m-0 text-[13px] font-bold tracking-[-0.01em] text-[#0E0E14]">
                    No Track Record Yet
                </p>

                <p className="m-0 max-w-[230px] text-[11px] leading-[1.45] text-[#595D68]">
                    {candidate?.first_name || 'This talent'} hasn't been invited
                    or hired through Wripple yet
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-1 flex-col justify-center px-4 py-2">
            <BadgeList performance={performance} demand={demand} />
        </div>
    );
};

/**
 * The profile section that shows the whole calculation. Must match the id on
 * `TalentProfileHireConfidence`, which is the slug of its nav title.
 */
const HIRE_CONFIDENCE_SECTION = 'hire-confidence';

/**
 * Hire Confidence blends the profile-against-role Role Match score with the AI
 * Engagement Fit assessment of the candidate's submitted proposal, so it only
 * unlocks once they have proposed and been assessed.
 *
 * `supportsProposals` is false on surfaces where no proposal is possible —
 * browsing talent outside a position — and the panel falls back to the badges
 * alone rather than promising an unlock that can never come.
 */
const CandidateHireConfidence = ({
    candidate,
    supportsProposals = true,
    isActive = true,
    profileLink,
    criteria,
}) => {
    // const { candidate } = useCandidate();

    // The two earned badges: track record and how sought-after they are. Each
    // is null when there is nothing to award, and the whole block drops when
    // neither has been earned.
    const performance = candidate?.performanceSummary?.();
    const demand = candidate?.demandSummary?.();

    if (!candidate?.hasEngagementFit?.()) {
        if (!supportsProposals) {
            return <HistoryOnlyPanel candidate={candidate} performance={performance} demand={demand} />;
        }

        // A proposal on the table with no assessment against it means the run
        // hasn't landed yet — which is waiting, not locked.
        return candidate?.hasProposalForClient?.()
            ? <PendingPanel candidate={candidate} />
            : <LockedPanel candidate={candidate} />;
    }

    // The blend that actually feeds 45% of Hire Confidence, so the row and the
    // ring above it reconcile — `roleMatchScore()` is the profile-only figure
    // the client-facing Match Score shows.
    const roleMatchScore = candidate.combinedRoleMatchScore?.();

    const showAssessment = (e) => {
        e.stopPropagation();
        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            closeOnBackdrop: true,
            content: <CandidateAssessmentDetails candidate={candidate} />
        });
    };

    // The same two halves the profile's Role Match accordion shows, from the
    // same component — see `CandidateRoleMatchDetails`.
    const showRoleMatch = (e) => {
        e.stopPropagation();
        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            // Read-only, so there is nothing to lose to a stray click outside.
            closeOnBackdrop: true,
            content: <CandidateRoleMatchDetails candidate={candidate} criteria={criteria} />
        });
    };

    // Deep-links straight to the section that shows the whole calculation
    // rather than the top of the profile, which is what the verdict promises.
    const breakdownLink = profileLink
        ? { ...profileLink, hash: `#${HIRE_CONFIDENCE_SECTION}` }
        : null;

    // The outer gap matches the inner one, so the badges sit the same distance
    // from the scores as the ring, verdict and scores sit from each other.
    return (
        <div className="relative flex flex-1 flex-col items-stretch justify-start gap-[5px] px-0 py-2 text-center">
            <div className="flex flex-col items-center gap-[7px] text-center">
                <FitRing
                    score={candidate.hireConfidenceScore(false)}
                    color={RING_PURPLE}
                    isActive={isActive}
                />

                <FitVerdict
                    to={breakdownLink}
                    title="See how this score was calculated"
                >
                    {candidate.hireConfidenceLabel()}
                </FitVerdict>

                {/* Only the two role-specific scores. Market Demand and Prior
                    Outcomes are told by the badges below instead — as earned
                    badges they need no number, and four rows of figures on a
                    tile this size stopped being scannable. */}
                <StatGrid>
                    <StatRow
                        hasInfo
                        label="Role Match"
                        value={roleMatchScore}
                        isActive={isActive}
                        onClick={showRoleMatch}
                        title="See how Role Match was scored"
                    />

                    {/* The whole row opens the assessment, not a link inside the
                        label: the target is now the width of the tile instead of
                        fourteen characters of text, so the most useful thing on
                        the tile is hard to miss rather than easy to skip. */}
                    <StatRow
                        hasInfo
                        isActive={isActive}
                        label="Engagement Fit"
                        value={candidate.engagementFitScore()}
                        onClick={showAssessment}
                        title="View the full AI assessment"
                    />
                </StatGrid>
            </div>

            {/* No margin of its own — `StatGrid` above owns the gap, so the
                spacing matches the Role Match tab exactly. */}
            {(performance || demand) && (
                <BadgeList performance={performance} demand={demand} />
            )}
        </div>
    )
}

export default CandidateHireConfidence;